import { useState } from 'react';
import { Ban, Building2, ListPlus, Cloud, Mail, Pencil, Sparkles, Star, Trash2, UserPlus } from 'lucide-react';
import toast from 'react-hot-toast';
import { Modal } from '@/components/ui/Modal';
import { callBackend, useMutation, useQueryState } from '@/lib/api-hooks';
import { backendApi } from '@/lib/backend';
import { getPhoneTypeLabel } from '@/utils/phoneUtils';
import type { ContactPhoneType } from '@/types';
import { useCrm } from './CrmContext';
import { CallButton } from './CallButton';
import { ContactEditor } from './ContactEditor';
import { TaskEditor } from './TaskEditor';
import { TaskItem } from './TaskList';
import { Timeline } from './Timeline';
import { ExtensionPicker, TagInput } from './fields';
import { relativeTime } from '@/lib/relative-time';
import { buttonClass, errorMessage, formatTalk, initials, inputClass } from './format';
import { LIFECYCLE_LABELS, type CrmContact, type CrmTask, type Lifecycle } from './types';

type ContactDetail = {
  contact: CrmContact;
  company: { id: string; name: string; vip: boolean } | null;
  openTasks: CrmTask[];
};

function Stat({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-lg bg-gray-50 dark:bg-gray-700/50 px-3 py-2">
      <p className="text-lg font-semibold text-gray-900 dark:text-white">{value}</p>
      <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
    </div>
  );
}

export function ContactPanel({ contactId, onClose }: { contactId: string; onClose: () => void }) {
  const { data, hasFeature, openCompany } = useCrm();
  const detail = useQueryState<ContactDetail>(backendApi.crm.getContact, { contactId });
  const updateFields = useMutation(backendApi.crm.updateContactFields);
  const deleteContact = useMutation(backendApi.crm.deleteContact);
  const setCallerStatus = useMutation(backendApi.crm.setCallerStatus);
  const [editing, setEditing] = useState(false);
  const [addingTask, setAddingTask] = useState(false);
  const [brief, setBrief] = useState<{ brief: string; suggestions: string[] } | null>(null);
  const [briefing, setBriefing] = useState(false);

  const contact = detail.data?.contact;
  const canManage = Boolean(data?.canManage);
  const isCaller = contact?.kind === 'caller';

  const quick = async (patch: Record<string, unknown>) => {
    try {
      await updateFields({ contactId, ...patch });
    } catch (error) {
      toast.error(errorMessage(error));
    }
  };

  const remove = async () => {
    if (!contact) return;
    if (!confirm(`Delete ${contact.name} from the CRM? Its notes and tasks are deleted too.`)) return;
    const removeFromPbx = Boolean(contact.yeastarContactId) && confirm('Also remove it from the PBX phone book?');
    try {
      await deleteContact({ contactId, removeFromPbx });
      toast.success('Contact deleted');
      onClose();
    } catch (error) {
      toast.error(errorMessage(error));
    }
  };

  const getBrief = async () => {
    setBriefing(true);
    try {
      setBrief(await callBackend(backendApi.crmAi.getBrief, { contactId }));
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setBriefing(false);
    }
  };

  const triage = async (status: 'ignored' | 'spam' | 'new') => {
    try {
      await setCallerStatus({ contactIds: [contactId], status });
      toast.success(status === 'spam' ? (hasFeature('spamBlocking') ? 'Marked as spam and blocked on the PBX' : 'Marked as spam') : status === 'ignored' ? 'Ignored' : 'Back in New callers');
    } catch (error) {
      toast.error(errorMessage(error));
    }
  };

  return (
    <Modal isOpen onClose={onClose} title={contact ? (isCaller ? 'Unknown caller' : 'Contact') : 'Contact'} size="xl">
      {detail.error && <p className="text-red-600">{detail.error}</p>}
      {!contact && !detail.error && <p className="text-gray-500">Loading…</p>}
      {contact && (
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          <div className="lg:col-span-2 space-y-5">
            <div className="flex items-start gap-3">
              <div className="w-14 h-14 shrink-0 rounded-full bg-primary-100 dark:bg-primary-900/50 text-primary-700 dark:text-primary-300 flex items-center justify-center text-lg font-semibold">
                {initials(contact.name)}
              </div>
              <div className="min-w-0">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white break-words">
                  {contact.name}
                  {canManage && !isCaller && (
                    <button type="button" onClick={() => void quick({ vip: !contact.vip })} className="ml-2 align-middle" title={contact.vip ? 'Remove VIP' : 'Mark as VIP'}>
                      <Star className={`w-5 h-5 ${contact.vip ? 'text-amber-500 fill-amber-400' : 'text-gray-300 hover:text-amber-400'}`} />
                    </button>
                  )}
                </h2>
                {contact.jobTitle && <p className="text-sm text-gray-600 dark:text-gray-300">{contact.jobTitle}</p>}
                {detail.data?.company && (
                  <button type="button" onClick={() => openCompany(detail.data!.company!.id)} className="text-sm text-primary-600 dark:text-primary-400 hover:underline inline-flex items-center gap-1">
                    <Building2 className="w-3.5 h-3.5" /> {detail.data.company.name}
                  </button>
                )}
                <div className="flex flex-wrap gap-1.5 mt-1 text-xs">
                  {contact.yeastarContactId && (
                    <span className="inline-flex items-center gap-1 text-gray-500 dark:text-gray-400" title="In the PBX phone book">
                      <Cloud className="w-3 h-3" /> Phone book{contact.syncStatus === 'error' ? ' (last update failed)' : contact.syncStatus === 'pending' ? ' (updating)' : ''}
                    </span>
                  )}
                  {contact.blocked && <span className="text-red-600">Blocked on the PBX</span>}
                </div>
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              {isCaller ? (
                canManage && (
                  <>
                    <button type="button" onClick={() => setEditing(true)} className={buttonClass.primary}>
                      <UserPlus className="w-4 h-4" /> Save as contact
                    </button>
                    {hasFeature('newCallerInbox') && (
                      <>
                        <button type="button" onClick={() => void triage('ignored')} className={buttonClass.secondary}>
                          Ignore
                        </button>
                        <button type="button" onClick={() => void triage(contact.callerStatus === 'spam' ? 'new' : 'spam')} className={buttonClass.danger}>
                          <Ban className="w-4 h-4" /> {contact.callerStatus === 'spam' ? 'Not spam' : 'Spam'}
                        </button>
                      </>
                    )}
                  </>
                )
              ) : (
                <>
                  {canManage && (
                    <button type="button" onClick={() => setEditing(true)} className={buttonClass.secondary}>
                      <Pencil className="w-4 h-4" /> Edit
                    </button>
                  )}
                  {canManage && (
                    <button type="button" onClick={() => setAddingTask(true)} className={buttonClass.secondary}>
                      <ListPlus className="w-4 h-4" /> Follow-up
                    </button>
                  )}
                  {hasFeature('aiAssistant') && (
                    <button type="button" onClick={() => void getBrief()} disabled={briefing} className={buttonClass.secondary}>
                      <Sparkles className="w-4 h-4" /> {briefing ? 'Thinking…' : 'Brief me'}
                    </button>
                  )}
                </>
              )}
            </div>

            {brief && (
              <div className="rounded-lg bg-purple-50 dark:bg-purple-900/20 p-3 text-sm text-purple-950 dark:text-purple-100">
                <p className="font-medium flex items-center gap-1 mb-1">
                  <Sparkles className="w-4 h-4" /> Before you call
                </p>
                <p>{brief.brief}</p>
                {brief.suggestions.length > 0 && (
                  <ul className="mt-2 list-disc pl-5 space-y-0.5">
                    {brief.suggestions.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            <div className="space-y-2">
              {contact.phones.map((phone, index) => (
                <div key={`${phone.number}-${index}`} className="flex items-center justify-between gap-2 p-2.5 rounded-lg bg-gray-50 dark:bg-gray-700/50">
                  <div className="min-w-0">
                    <p className="text-xs text-gray-500 dark:text-gray-400">{getPhoneTypeLabel(phone.type as ContactPhoneType)}</p>
                    <p className="font-medium text-gray-900 dark:text-white truncate">{phone.number}</p>
                  </div>
                  <CallButton number={phone.number} size="sm" label="Call" />
                </div>
              ))}
              {contact.email && (
                <a href={`mailto:${contact.email}`} className="flex items-center gap-2 p-2.5 rounded-lg bg-gray-50 dark:bg-gray-700/50 text-sm text-primary-600 dark:text-primary-400 hover:underline">
                  <Mail className="w-4 h-4" /> {contact.email}
                </a>
              )}
            </div>

            {!isCaller && (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Stage</p>
                    <select
                      disabled={!canManage}
                      value={contact.lifecycle ?? ''}
                      onChange={(event) => void quick({ lifecycle: (event.target.value as Lifecycle) || null })}
                      className={inputClass}
                    >
                      <option value="">Not set</option>
                      {Object.entries(LIFECYCLE_LABELS).map(([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Account owner</p>
                    {canManage ? (
                      <ExtensionPicker value={contact.ownerExtension} onChange={(extension, name) => void quick({ ownerExtension: extension || null, ownerName: name })} />
                    ) : (
                      <p className="text-sm text-gray-900 dark:text-white">{contact.ownerName ?? contact.ownerExtension ?? 'Unassigned'}</p>
                    )}
                  </div>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Tags</p>
                  {canManage ? (
                    <TagInput value={contact.tags} onChange={(tags) => void quick({ tags })} />
                  ) : (
                    <p className="text-sm text-gray-900 dark:text-white">{contact.tags.join(', ') || '—'}</p>
                  )}
                </div>
                {(data?.settings.customFields ?? []).filter((field) => contact.customFields[field.key]).map((field) => (
                  <div key={field.key}>
                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400">{field.label}</p>
                    <p className="text-sm text-gray-900 dark:text-white">{contact.customFields[field.key]}</p>
                  </div>
                ))}
                {contact.remark && (
                  <div>
                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Phone book remark</p>
                    <p className="text-sm text-gray-900 dark:text-white whitespace-pre-line">{contact.remark}</p>
                  </div>
                )}
              </div>
            )}

            <div className="grid grid-cols-2 gap-2">
              <Stat label="Calls" value={contact.stats.totalInteractions} />
              <Stat label="Missed (inbound)" value={contact.stats.missedCalls} />
              <Stat label="Talk time" value={formatTalk(contact.stats.totalDuration)} />
              <Stat label="Last call" value={contact.stats.lastInteractionTimestamp ? relativeTime(new Date(contact.stats.lastInteractionTimestamp * 1000)) : '—'} />
            </div>

            {canManage && (
              <button type="button" onClick={() => void remove()} className="inline-flex items-center gap-1.5 text-sm text-red-600 hover:underline">
                <Trash2 className="w-4 h-4" /> Delete {isCaller ? 'caller' : 'contact'}
              </button>
            )}
          </div>

          <div className="lg:col-span-3 space-y-5">
            {(detail.data?.openTasks.length ?? 0) > 0 && (
              <div className="space-y-2">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Open tasks</h3>
                {detail.data!.openTasks.map((task) => (
                  <TaskItem key={task.id} task={task} showSubject={false} />
                ))}
              </div>
            )}
            <div>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">History</h3>
              <Timeline contactId={contactId} />
            </div>
          </div>
        </div>
      )}
      {editing && contact && <ContactEditor contact={contact} onClose={() => setEditing(false)} />}
      {addingTask && contact && <TaskEditor contactId={contact.id} subjectName={contact.name} onClose={() => setAddingTask(false)} />}
    </Modal>
  );
}
